import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Calendar,
  Plus,
  Search,
  Filter,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  MapPin,
  User,
  Tractor,
  Leaf,
  Droplet,
  Wheat,
  Shovel,
  Scissors,
  MoreHorizontal,
  Image as ImageIcon,
  Video,
  Upload,
  Trash2,
  Eye,
  Edit,
  ChevronDown,
  ChevronUp,
  Sun,
  Cloud,
  CloudRain,
  Wind,
  Thermometer,
  Play,
  CheckCircle2,
  Calculator,
  Info,
  Minus,
  X,
  Bell,
  FileSignature,
  Mail,
  Smartphone,
  Truck,
  FileText,
  Package,
  ClipboardList,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icons mapping pour les types d'opérations
const OPERATION_ICONS = {
  travail_sol: Shovel,
  semis: Leaf,
  traitement: Droplet,
  fertilisation: Leaf,
  irrigation: Droplet,
  recolte: Wheat,
  entretien: Scissors,
  autre: MoreHorizontal,
};

const METEO_ICONS = {
  ensoleille: Sun,
  nuageux: Cloud,
  pluie: CloudRain,
  vent: Wind,
};

const STATUT_COLORS = {
  previsionnelle: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  realisee: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  annulee: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const PRIORITE_COLORS = {
  basse: 'bg-gray-100 text-gray-600',
  normale: 'bg-blue-100 text-blue-600',
  haute: 'bg-orange-100 text-orange-600',
  urgente: 'bg-red-100 text-red-600',
};

// ==================== CALCULETTE PRODUIT POUR OPÉRATEURS ====================
// Interface simple avec gros chiffres pour le terrain
const CalculetteProduit = ({ produit, onClose, volumeBouillie }) => {
  const [volumeCuve, setVolumeCuve] = useState('1000'); // Volume pulvé en L
  const [volumeBouillieHa, setVolumeBouillieHa] = useState(volumeBouillie || '200'); // L/ha
  const [doseHa, setDoseHa] = useState(produit?.quantite || produit?.dose_recommandee || ''); // Dose produit L/ha
  
  // Calculs
  const volumeCuveNum = parseFloat(volumeCuve) || 0;
  const volumeBouillieNum = parseFloat(volumeBouillieHa) || 200;
  const doseNum = parseFloat(doseHa) || 0;
  
  // Hectares traitables avec cette cuve
  const hectaresTraitables = volumeBouillieNum > 0 ? (volumeCuveNum / volumeBouillieNum) : 0;
  
  // Quantité de produit à mettre dans la cuve
  const quantiteProduit = hectaresTraitables * doseNum;
  
  // Concentration en mL/L de bouillie (pour vérification)
  const concentrationMlL = volumeBouillieNum > 0 ? (doseNum * 1000 / volumeBouillieNum) : 0;
  
  return (
    <div className="fixed inset-0 z-[400] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-background rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-primary text-primary-foreground p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calculator className="w-8 h-8" />
            <div>
              <h2 className="text-xl font-bold">Calculette Pulvé</h2>
              {produit?.nom && (
                <p className="text-sm opacity-90">{produit.nom}</p>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-primary-foreground hover:bg-white/20">
            <X className="w-6 h-6" />
          </Button>
        </div>
        
        {/* Fiche produit si sélectionné */}
        {produit?.numero_amm && (
          <div className="bg-green-50 dark:bg-green-950/30 p-3 border-b">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-green-700 dark:text-green-400">{produit.nom}</p>
                <p className="text-green-600 dark:text-green-500 text-xs">AMM: {produit.numero_amm} {produit.firme && `• ${produit.firme}`}</p>
                {produit.dose_max && (
                  <p className="text-xs mt-1">Dose max homologuée: <span className="font-bold">{produit.dose_max} {produit.unite || 'L/ha'}</span></p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Entrées avec GROS chiffres */}
        <div className="p-4 space-y-4">
          {/* Volume cuve */}
          <div className="space-y-2">
            <Label className="text-base font-semibold flex items-center gap-2">
              🚜 Volume de ma cuve (litres)
            </Label>
            <div className="flex gap-2">
              {['400', '600', '1000', '1500', '2000', '3000'].map(vol => (
                <Button
                  key={vol}
                  variant={volumeCuve === vol ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setVolumeCuve(vol)}
                  className="flex-1 text-lg font-bold"
                >
                  {vol}
                </Button>
              ))}
            </div>
            <Input
              type="number"
              value={volumeCuve}
              onChange={e => setVolumeCuve(e.target.value)}
              className="text-center text-3xl font-bold h-16"
              placeholder="1000"
            />
          </div>
          
          {/* Volume bouillie/ha */}
          <div className="space-y-2">
            <Label className="text-base font-semibold flex items-center gap-2">
              💧 Volume bouillie (L/ha)
            </Label>
            <div className="flex gap-2">
              {['100', '150', '200', '300', '400'].map(vol => (
                <Button
                  key={vol}
                  variant={volumeBouillieHa === vol ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setVolumeBouillieHa(vol)}
                  className="flex-1"
                >
                  {vol}
                </Button>
              ))}
            </div>
            <Input
              type="number"
              value={volumeBouillieHa}
              onChange={e => setVolumeBouillieHa(e.target.value)}
              className="text-center text-2xl font-bold h-14"
              placeholder="200"
            />
          </div>
          
          {/* Dose produit/ha */}
          <div className="space-y-2">
            <Label className="text-base font-semibold flex items-center gap-2">
              🧪 Dose produit ({produit?.unite || 'L/ha'})
            </Label>
            {produit?.dose_recommandee && (
              <p className="text-xs text-muted-foreground">
                Recommandé: {produit.dose_recommandee} {produit.unite || 'L/ha'}
                {produit.dose_max && ` (max: ${produit.dose_max})`}
              </p>
            )}
            <Input
              type="number"
              step="0.1"
              value={doseHa}
              onChange={e => setDoseHa(e.target.value)}
              className={`text-center text-3xl font-bold h-16 ${
                produit?.dose_max && parseFloat(doseHa) > produit.dose_max 
                  ? 'border-red-500 bg-red-50 text-red-700' 
                  : ''
              }`}
              placeholder="2.5"
            />
            {produit?.dose_max && parseFloat(doseHa) > produit.dose_max && (
              <p className="text-red-600 text-sm font-semibold">⚠️ DOSE DÉPASSÉE !</p>
            )}
          </div>
        </div>
        
        {/* RÉSULTAT avec très gros chiffres */}
        <div className="bg-primary/10 p-6 border-t-4 border-primary">
          <div className="text-center space-y-4">
            {/* Hectares traitables */}
            <div>
              <p className="text-sm text-muted-foreground mb-1">Avec cette cuve je traite</p>
              <p className="text-4xl font-black text-primary">
                {hectaresTraitables.toFixed(1)} ha
              </p>
            </div>
            
            {/* Quantité produit - LE RÉSULTAT PRINCIPAL */}
            <div className="bg-primary text-primary-foreground rounded-xl p-4">
              <p className="text-sm opacity-90 mb-1">JE METS DANS LA CUVE</p>
              <p className="text-6xl font-black">
                {quantiteProduit.toFixed(1)}
              </p>
              <p className="text-2xl font-bold">{produit?.unite === 'kg/ha' ? 'kg' : 'litres'} de produit</p>
            </div>
            
            {/* Info concentration */}
            <p className="text-xs text-muted-foreground">
              Concentration: {concentrationMlL.toFixed(1)} mL/L de bouillie
            </p>
          </div>
        </div>
        
        {/* Boutons */}
        <div className="p-4 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Fermer
          </Button>
          <Button 
            className="flex-1 text-lg font-bold"
            onClick={() => {
              toast.success(`✅ ${quantiteProduit.toFixed(1)} L pour ${hectaresTraitables.toFixed(1)} ha`);
              onClose();
            }}
          >
            <CheckCircle2 className="w-5 h-5 mr-2" />
            OK, c'est noté !
          </Button>
        </div>
      </div>
    </div>
  );
};

// Composant pour afficher une intervention
const InterventionCard = ({ intervention, operations, onEdit, onRealiser, onValider, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = OPERATION_ICONS[intervention.categorie_operation] || MoreHorizontal;
  const MeteoIcon = METEO_ICONS[intervention.temps] || Sun;

  return (
    <Card 
      className="card-hover" 
      data-testid={`intervention-card-${intervention.id}`}
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ 
                backgroundColor: operations[intervention.categorie_operation]?.color + '20',
                color: operations[intervention.categorie_operation]?.color 
              }}
            >
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold">{intervention.operation}</h3>
              <p className="text-sm text-muted-foreground">{intervention.nom_parcelle}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={STATUT_COLORS[intervention.statut_intervention]}>
                  {intervention.statut_intervention === 'previsionnelle' ? 'Prévisionnelle' : 
                   intervention.statut_intervention === 'realisee' ? 'Réalisée' : 'Annulée'}
                </Badge>
                {intervention.priorite && intervention.priorite !== 'normale' && (
                  <Badge className={PRIORITE_COLORS[intervention.priorite]}>
                    {intervention.priorite}
                  </Badge>
                )}
                {intervention.validee && (
                  <Badge className="bg-purple-100 text-purple-600">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Validée
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>

        {/* Infos de base */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span>{new Date(intervention.date_debut).toLocaleDateString('fr-FR')}</span>
          </div>
          {intervention.surface_travaillee && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span>{intervention.surface_travaillee} ha</span>
            </div>
          )}
          {intervention.operateur_nom && (
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-muted-foreground" />
              <span>{intervention.operateur_nom}</span>
            </div>
          )}
          {intervention.temps && (
            <div className="flex items-center gap-2">
              <MeteoIcon className="w-4 h-4 text-muted-foreground" />
              <span>{intervention.temps}</span>
              {intervention.temperature && <span>({intervention.temperature}°C)</span>}
            </div>
          )}
        </div>

        {/* Médias */}
        {(intervention.images?.length > 0 || intervention.videos?.length > 0) && (
          <div className="mt-4 flex items-center gap-2">
            {intervention.images?.length > 0 && (
              <Badge variant="outline">
                <ImageIcon className="w-3 h-3 mr-1" />
                {intervention.images.length} photo(s)
              </Badge>
            )}
            {intervention.videos?.length > 0 && (
              <Badge variant="outline">
                <Video className="w-3 h-3 mr-1" />
                {intervention.videos.length} vidéo(s)
              </Badge>
            )}
          </div>
        )}

        {/* Détails expandables */}
        {expanded && (
          <div className="mt-4 pt-4 border-t space-y-4">
            {/* Programmateur */}
            {intervention.programmee_par_nom && (
              <div className="text-sm">
                <span className="text-muted-foreground">Programmée par : </span>
                <span className="font-medium">{intervention.programmee_par_nom}</span>
              </div>
            )}

            {/* Commentaire */}
            {intervention.commentaire && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Commentaire</p>
                <p className="text-sm bg-muted/50 p-2 rounded">{intervention.commentaire}</p>
              </div>
            )}

            {/* Images */}
            {intervention.images?.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-2">Photos</p>
                <div className="flex gap-2 flex-wrap">
                  {intervention.images.map((img, idx) => (
                    <img 
                      key={idx} 
                      src={`${API_URL}${img}`} 
                      alt={`Photo ${idx + 1}`}
                      className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80"
                      onClick={() => window.open(`${API_URL}${img}`, '_blank')}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2">
              {intervention.statut_intervention === 'previsionnelle' && (
                <Button 
                  size="sm" 
                  onClick={() => onRealiser(intervention.id)}
                  data-testid={`btn-realiser-${intervention.id}`}
                >
                  <Play className="w-4 h-4 mr-1" />
                  Marquer réalisée
                </Button>
              )}
              {intervention.statut_intervention === 'realisee' && !intervention.validee && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => onValider(intervention.id)}
                  data-testid={`btn-valider-${intervention.id}`}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Valider
                </Button>
              )}
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onEdit(intervention)}
              >
                <Edit className="w-4 h-4 mr-1" />
                Modifier
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                onClick={() => onDelete(intervention.id)}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Supprimer
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Dialog pour créer/modifier une intervention
const InterventionDialog = ({
  open,
  onOpenChange,
  intervention,
  parcelles,
  collaborateurs,
  operations,
  materiels,
  intrants,
  commandes = [],
  onSave,
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    parcelle_id: '', // Pour rétrocompatibilité
    parcelles_selection: [], // Nouveau: multi-parcelles [{id, nom, surface, exploitation_nom, culture, searchQuery, showSuggestions}]
    date: new Date().toISOString().split('T')[0],
    date_previsionnelle: '',
    heure_debut: '',
    heure_fin: '',
    type_operation: '',
    operation: '',
    surface_travaillee: '',
    statut_intervention: 'previsionnelle',
    priorite: 'normale',
    operateur_id: '',
    operateur_nom: '',
    programmee_par_id: '',
    programmee_par_nom: '',
    duree_heures: '',
    duree_previsionnelle: '', // Durée prévisionnelle de l'intervention
    meteo: '',
    temperature: '',
    vent_kmh: '',
    hygrometrie: '',
    commentaire: '',
    avec_intrant: false,
    intrants: [],
    avec_materiel: false,
    materiels_ids: [], // IDs des matériels sélectionnés
    materiels_noms: [], // Noms pour affichage
    produits: [],
    cibles: [], // Cibles de traitement (ravageurs, maladies, adventices)
    suggestions_produits: [], // Suggestions IA de produits basées sur les cibles
    images: [],
    videos: [],
    // Notifications
    notifier_operateur: true, // Notifier l'opérateur par email/push
    notifier_client: false, // Notifier le client par email
    client_email: '', // Email du client si notification activée
    // Signature électronique
    demander_signature: false, // Demander une signature de réception
    type_signature: 'email', // 'email' ou 'direct' (sur téléphone/tablette)
    // Données traitement phyto
    volume_bouillie_ha: '',
    pression_bars: '',
    vitesse_avancement: '',
    type_buse: '',
    // Données fertilisation (cahier)
    fertilisation: {
      type_apport: '', // organique, mineral, mixte
      forme_azote: '', // ammonitrate, uree, solution_azotee, organique
      azote_total_kg: '',
      azote_efficace_kg: '',
      phosphore_p2o5_kg: '',
      potasse_k2o_kg: '',
      magnesie_mgo_kg: '',
      soufre_so3_kg: '',
      calcium_cao_kg: '',
      oligo_elements: '',
      matiere_organique_t: '',
      c_n_ratio: '',
      enfouissement: false,
      delai_enfouissement_h: '',
      methode_epandage: '', // centrifuge, rampe, localisé, fertigation
      objectif_rendement: '',
      reliquat_sortie_hiver: '',
      mineralisation_humus: '',
      apport_precedent: '',
    },
    // Transport / Livraison / Chargement
    transport: {
      lieu_depart: '',
      lieu_arrivee: '',
      marchandise: '',
      quantite: '',
      vehicule: '',
      chauffeur: '',
      bon_livraison_requis: false,
      lettre_voiture_requise: false,
    }
  });
  
  const [saving, setSaving] = useState(false);
  const [showCalculette, setShowCalculette] = useState(null); // null ou {produit, idx}
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [selectedOperations, setSelectedOperations] = useState([]);
  const [parcelleSearch, setParcelleSearch] = useState('');
  const [showParcelleDropdown, setShowParcelleDropdown] = useState(false);
  const [cibleSearch, setCibleSearch] = useState('');
  const [showCibleDropdown, setShowCibleDropdown] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  useEffect(() => {
    if (intervention) {
      // Convertir parcelle_id unique en parcelles_selection pour rétrocompatibilité
      let parcelles_selection_init = intervention.parcelles_selection || [];
      if (parcelles_selection_init.length === 0 && intervention.parcelle_id) {
        const p = parcelles.find(p => p.id === intervention.parcelle_id);
        if (p) {
          parcelles_selection_init = [{
            id: p.id,
            nom: p.nom,
            surface: p.surface_ha || p.surface,
            exploitation_nom: p.exploitation_nom,
            culture: p.culture_actuelle || p.culture || p.culture_nom,
            searchQuery: p.nom
          }];
        }
      }
      
      setFormData({
        parcelle_id: intervention.parcelle_id || '',
        parcelles_selection: parcelles_selection_init,
        date: intervention.date_debut || new Date().toISOString().split('T')[0],
        date_previsionnelle: intervention.date_previsionnelle || '',
        heure_debut: intervention.heure_debut || '',
        heure_fin: intervention.heure_fin || '',
        type_operation: intervention.categorie_operation || '',
        operation: intervention.operation || '',
        surface_travaillee: intervention.surface_travaillee || '',
        statut_intervention: intervention.statut_intervention || 'previsionnelle',
        priorite: intervention.priorite || 'normale',
        operateur_id: intervention.operateur_id || '',
        operateur_nom: intervention.operateur_nom || '',
        programmee_par_id: intervention.programmee_par_id || '',
        programmee_par_nom: intervention.programmee_par_nom || '',
        duree_heures: intervention.duree_heures || '',
        meteo: intervention.temps || '',
        temperature: intervention.temperature || '',
        commentaire: intervention.commentaire || '',
        avec_intrant: intervention.avec_intrant || false,
        intrants: intervention.intrants || [],
        avec_materiel: intervention.avec_materiel || false,
        materiels: intervention.materiels || [],
        produits: intervention.produits || [],
        cibles: intervention.cibles || [],
        suggestions_produits: [],
        images: intervention.images || [],
        videos: intervention.videos || [],
      });
      if (intervention.categorie_operation && operations[intervention.categorie_operation]) {
        setSelectedOperations(operations[intervention.categorie_operation].operations || []);
      }
    } else {
      setFormData({
        parcelle_id: '',
        parcelles_selection: [],
        date: new Date().toISOString().split('T')[0],
        date_previsionnelle: '',
        heure_debut: '',
        heure_fin: '',
        type_operation: '',
        operation: '',
        surface_travaillee: '',
        statut_intervention: 'previsionnelle',
        priorite: 'normale',
        operateur_id: '',
        operateur_nom: '',
        programmee_par_id: user?.id || '',
        programmee_par_nom: user ? `${user.prenom} ${user.nom}` : '',
        duree_heures: '',
        meteo: '',
        temperature: '',
        commentaire: '',
        avec_intrant: false,
        intrants: [],
        avec_materiel: false,
        materiels: [],
        produits: [],
        cibles: [],
        suggestions_produits: [],
        images: [],
        videos: [],
      });
      setSelectedOperations([]);
    }
  }, [intervention, open, operations, user, parcelles]);

  // Mettre à jour les opérations quand le type change
  useEffect(() => {
    if (formData.type_operation && operations[formData.type_operation]) {
      setSelectedOperations(operations[formData.type_operation].operations || []);
    }
  }, [formData.type_operation, operations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Vérifier qu'au moins une parcelle est sélectionnée (nouveau système multi-parcelles)
    const hasMultiParcelles = (formData.parcelles_selection || []).filter(p => p.id).length > 0;
    const hasSingleParcelle = formData.parcelle_id;
    
    if ((!hasMultiParcelles && !hasSingleParcelle) || !formData.type_operation || !formData.operation) {
      toast.error('Veuillez remplir tous les champs obligatoires (au moins une parcelle)');
      return;
    }
    
    // Si multi-parcelles, définir parcelle_id sur la première pour rétrocompatibilité
    const dataToSave = { ...formData };
    if (hasMultiParcelles && !hasSingleParcelle) {
      dataToSave.parcelle_id = formData.parcelles_selection[0].id;
    }
    
    // Calculer la surface totale si multi-parcelles
    if (hasMultiParcelles && !dataToSave.surface_travaillee) {
      const totalSurface = formData.parcelles_selection
        .filter(p => p.id && p.surface)
        .reduce((sum, p) => sum + parseFloat(p.surface || 0), 0);
      dataToSave.surface_travaillee = totalSurface.toFixed(2);
    }
    
    setSaving(true);
    await onSave(dataToSave, intervention?.id);
    setSaving(false);
    onOpenChange(false);
  };

  // Calculer la surface totale des parcelles sélectionnées
  const calculateTotalSurface = () => {
    return (formData.parcelles_selection || [])
      .filter(p => p.id && p.surface)
      .reduce((sum, p) => sum + parseFloat(p.surface || 0), 0)
      .toFixed(2);
  };

  // Sélectionner une parcelle et pré-remplir la surface (ancien système - gardé pour compatibilité)
  const handleParcelleChange = (parcelleId) => {
    const parcelle = parcelles.find(p => p.id === parcelleId);
    setFormData(prev => ({
      ...prev,
      parcelle_id: parcelleId,
      surface_travaillee: parcelle?.surface || ''
    }));
  };

  // Sélectionner un opérateur
  const handleOperateurChange = (operateurId) => {
    const collab = collaborateurs.find(c => c.id === operateurId);
    setFormData(prev => ({
      ...prev,
      operateur_id: operateurId,
      operateur_nom: collab?.nom_complet || ''
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {intervention ? 'Modifier l\'intervention' : 'Nouvelle intervention'}
          </DialogTitle>
          <DialogDescription>
            Enregistrez une intervention agricole sur vos parcelles
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Statut et Priorité */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Statut *</Label>
              <Select 
                value={formData.statut_intervention}
                onValueChange={(v) => setFormData(prev => ({ ...prev, statut_intervention: v }))}
              >
                <SelectTrigger data-testid="select-statut">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="previsionnelle">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-500" />
                      Prévisionnelle
                    </div>
                  </SelectItem>
                  <SelectItem value="realisee">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Réalisée
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priorité</Label>
              <Select 
                value={formData.priorite}
                onValueChange={(v) => setFormData(prev => ({ ...prev, priorite: v }))}
              >
                <SelectTrigger data-testid="select-priorite">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basse">Basse</SelectItem>
                  <SelectItem value="normale">Normale</SelectItem>
                  <SelectItem value="haute">Haute</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Parcelles - Multi-sélection groupée par exploitation */}
          <div className="space-y-2">
            <Label>Parcelles * <span className="text-xs text-muted-foreground">(plusieurs parcelles possibles, même de différentes exploitations)</span></Label>
            <div className="relative">
              <Input
                data-testid="search-parcelle"
                placeholder="Rechercher par nom, culture ou exploitation..."
                value={parcelleSearch}
                onChange={(e) => {
                  setParcelleSearch(e.target.value);
                  setShowParcelleDropdown(e.target.value.length >= 1);
                }}
                onFocus={() => setShowParcelleDropdown(parcelleSearch.length >= 1)}
                onBlur={() => setTimeout(() => setShowParcelleDropdown(false), 200)}
              />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />

              {/* Dropdown : parcelles groupées par exploitation */}
              {showParcelleDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-72 overflow-y-auto">
                  {(() => {
                    const search = parcelleSearch.toLowerCase();
                    const matched = parcelles.filter(p => {
                      if (search.length < 1) return true;
                      return p.nom?.toLowerCase().includes(search) ||
                        p.culture_actuelle?.toLowerCase().includes(search) ||
                        p.culture?.toLowerCase().includes(search) ||
                        p.culture_nom?.toLowerCase().includes(search) ||
                        p.exploitation_nom?.toLowerCase().includes(search);
                    });
                    if (matched.length === 0) {
                      return <div className="px-3 py-4 text-center text-sm text-muted-foreground">Aucune parcelle trouvée</div>;
                    }
                    // Grouper par exploitation
                    const grouped = matched.reduce((acc, p) => {
                      const k = p.exploitation_nom || 'Sans exploitation';
                      (acc[k] = acc[k] || []).push(p);
                      return acc;
                    }, {});
                    const selectedIds = new Set((formData.parcelles_selection || []).map(p => p.id));
                    return Object.entries(grouped).map(([explName, items]) => (
                      <div key={explName}>
                        <div className="sticky top-0 px-3 py-1.5 bg-primary/10 text-primary text-xs font-semibold border-b border-primary/20">
                          🏡 {explName} <span className="text-muted-foreground font-normal">({items.length})</span>
                        </div>
                        {items.slice(0, 30).map((p) => {
                          const isSel = selectedIds.has(p.id);
                          return (
                            <div
                              key={p.id}
                              className={`px-3 py-2 cursor-pointer hover:bg-accent text-sm ${isSel ? 'bg-accent/60' : ''}`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                if (isSel) {
                                  // Désélection
                                  setFormData(prev => ({
                                    ...prev,
                                    parcelles_selection: (prev.parcelles_selection || []).filter(x => x.id !== p.id),
                                  }));
                                } else {
                                  // Ajout
                                  setFormData(prev => ({
                                    ...prev,
                                    parcelles_selection: [
                                      ...(prev.parcelles_selection || []),
                                      {
                                        id: p.id,
                                        nom: p.nom,
                                        surface: p.surface_ha || p.surface || 0,
                                        exploitation_nom: p.exploitation_nom,
                                        culture: p.culture_actuelle || p.culture || p.culture_nom,
                                      }
                                    ],
                                    parcelle_id: prev.parcelle_id || p.id,
                                  }));
                                }
                              }}
                            >
                              <div className="font-medium flex items-center gap-2 justify-between">
                                <span className="flex items-center gap-2">
                                  <input type="checkbox" checked={isSel} readOnly className="w-4 h-4" />
                                  <span>{p.nom}</span>
                                  {(p.culture_actuelle || p.culture || p.culture_nom) && (
                                    <Badge variant="outline" className="text-xs font-normal">
                                      {p.culture_actuelle || p.culture || p.culture_nom}
                                    </Badge>
                                  )}
                                </span>
                                <span className="text-xs text-muted-foreground">{p.surface_ha || p.surface || '?'} ha</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
            {/* Parcelles sélectionnées (groupées par exploitation) */}
            {(formData.parcelles_selection || []).length > 0 && (
              <div className="mt-2 space-y-2" data-testid="parcelles-selectionnees">
                {(() => {
                  const grouped = (formData.parcelles_selection || []).reduce((acc, p) => {
                    const k = p.exploitation_nom || 'Sans exploitation';
                    (acc[k] = acc[k] || []).push(p);
                    return acc;
                  }, {});
                  const totalSurface = (formData.parcelles_selection || [])
                    .reduce((sum, p) => sum + parseFloat(p.surface || 0), 0);
                  return (
                    <>
                      <div className="text-xs text-muted-foreground flex items-center justify-between">
                        <span>{(formData.parcelles_selection || []).length} parcelle(s) · {Object.keys(grouped).length} exploitation(s)</span>
                        <span className="font-semibold">{totalSurface.toFixed(2)} ha total</span>
                      </div>
                      {Object.entries(grouped).map(([explName, items]) => (
                        <div key={explName} className="border rounded-md p-2 bg-muted/30">
                          <div className="text-xs font-semibold text-primary mb-1">🏡 {explName}</div>
                          <div className="flex flex-wrap gap-1">
                            {items.map(p => (
                              <Badge key={p.id} variant="secondary" className="gap-1">
                                <MapPin className="w-3 h-3" />
                                {p.nom} ({p.surface || '?'}ha)
                                <button
                                  type="button"
                                  className="ml-1 hover:text-destructive"
                                  onClick={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      parcelles_selection: (prev.parcelles_selection || []).filter(x => x.id !== p.id),
                                      parcelle_id: prev.parcelle_id === p.id ? '' : prev.parcelle_id,
                                    }));
                                  }}
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Type et Opération */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type d'opération *</Label>
              <Select 
                value={formData.type_operation}
                onValueChange={(v) => setFormData(prev => ({ ...prev, type_operation: v, operation: '' }))}
              >
                <SelectTrigger data-testid="select-type-operation">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(operations).map(([key, op]) => {
                    const Icon = OPERATION_ICONS[key] || MoreHorizontal;
                    return (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" style={{ color: op.color }} />
                          {op.nom}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Opération *</Label>
              <Select 
                value={formData.operation}
                onValueChange={(v) => setFormData(prev => ({ ...prev, operation: v }))}
                disabled={!formData.type_operation}
              >
                <SelectTrigger data-testid="select-operation">
                  <SelectValue placeholder="Choisir" />
                </SelectTrigger>
                <SelectContent>
                  {selectedOperations.map((op) => (
                    <SelectItem key={op} value={op}>{op}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Dates et Heures */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                required
                data-testid="input-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Date prévue</Label>
              <Input
                type="date"
                value={formData.date_previsionnelle}
                onChange={(e) => setFormData(prev => ({ ...prev, date_previsionnelle: e.target.value }))}
                data-testid="input-date-prev"
              />
            </div>
            <div className="space-y-2">
              <Label>Heure début</Label>
              <Input
                type="time"
                value={formData.heure_debut}
                onChange={(e) => setFormData(prev => ({ ...prev, heure_debut: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Heure fin</Label>
              <Input
                type="time"
                value={formData.heure_fin}
                onChange={(e) => setFormData(prev => ({ ...prev, heure_fin: e.target.value }))}
              />
            </div>
          </div>

          {/* Surface et Durée */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Surface travaillée (ha)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.surface_travaillee}
                onChange={(e) => setFormData(prev => ({ ...prev, surface_travaillee: e.target.value }))}
                placeholder="Toute la parcelle"
              />
            </div>
            <div className="space-y-2">
              <Label>Durée (heures)</Label>
              <Input
                type="number"
                step="0.5"
                value={formData.duree_heures}
                onChange={(e) => setFormData(prev => ({ ...prev, duree_heures: e.target.value }))}
                placeholder="Ex: 2.5"
              />
            </div>
          </div>

          {/* Opérateur */}
          <div className="space-y-2">
            <Label>Opérateur</Label>
            <Select 
              value={formData.operateur_id}
              onValueChange={handleOperateurChange}
            >
              <SelectTrigger data-testid="select-operateur">
                <SelectValue placeholder="Qui réalise l'intervention ?" />
              </SelectTrigger>
              <SelectContent>
                {collaborateurs.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.nom_complet} ({c.role_info?.nom || c.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Section Matériel utilisé */}
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Tractor className="w-4 h-4" />
                Matériel utilisé
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    materiels_selection: [...(prev.materiels_selection || []), { id: '', nom: '', searchQuery: '', showSuggestions: true }]
                  }));
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                Matériel ({materiels?.length || 0})
              </Button>
            </div>
            
            {/* Liste des matériels sélectionnés */}
            {(formData.materiels_selection || []).length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-3 bg-muted/50 rounded">
                Aucun matériel sélectionné. Cliquez sur "+ Matériel" pour en ajouter. 
                {materiels?.length === 0 && <span className="text-red-500 block mt-1">⚠️ Aucun matériel chargé - vérifiez la connexion</span>}
              </p>
            ) : (
              <div className="space-y-2">
                {(formData.materiels_selection || []).map((selection, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <Input
                        className="h-9 pr-8"
                        placeholder="Tapez pour rechercher un matériel..."
                        value={selection.searchQuery || selection.nom || ''}
                        onChange={(e) => {
                          const query = e.target.value;
                          setFormData(prev => {
                            const updated = [...(prev.materiels_selection || [])];
                            updated[idx] = {
                              ...updated[idx],
                              searchQuery: query,
                              showSuggestions: true
                            };
                            // Si on efface, reset l'ID
                            if (!query) {
                              updated[idx].id = '';
                              updated[idx].nom = '';
                            }
                            return { ...prev, materiels_selection: updated };
                          });
                        }}
                        onFocus={() => {
                          setFormData(prev => {
                            const updated = [...(prev.materiels_selection || [])];
                            updated[idx] = { ...updated[idx], showSuggestions: true };
                            return { ...prev, materiels_selection: updated };
                          });
                        }}
                        onBlur={() => {
                          setTimeout(() => {
                            setFormData(prev => {
                              const updated = [...(prev.materiels_selection || [])];
                              updated[idx] = { ...updated[idx], showSuggestions: false };
                              return { ...prev, materiels_selection: updated };
                            });
                          }, 200);
                        }}
                      />
                      {selection.id && (
                        <CheckCircle2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                      )}
                      
                      {/* Suggestions de matériels */}
                      {selection.showSuggestions && (
                        <div className="absolute z-[9999] w-full mt-1 bg-white dark:bg-gray-900 border rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {(!materiels || materiels.length === 0) ? (
                            <p className="px-3 py-2 text-sm text-red-500">⚠️ Aucun matériel disponible - rechargez la page</p>
                          ) : (
                            <>
                              {materiels
                                .filter(m => {
                                  const query = (selection.searchQuery || '').toLowerCase();
                                  if (!query) return true;
                                  return m.nom?.toLowerCase().includes(query) || 
                                         m.type?.toLowerCase().includes(query) ||
                                         m.marque?.toLowerCase().includes(query);
                                })
                                .slice(0, 15)
                                .map((m) => (
                                  <div
                                    key={m.id}
                                    className="px-3 py-2 hover:bg-accent cursor-pointer text-sm flex items-center justify-between"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      setFormData(prev => {
                                        const updated = [...(prev.materiels_selection || [])];
                                        updated[idx] = {
                                          id: m.id,
                                          nom: m.nom,
                                          type: m.type,
                                          searchQuery: m.nom,
                                          showSuggestions: false
                                        };
                                        return { ...prev, materiels_selection: updated };
                                      });
                                    }}
                                  >
                                    <div>
                                      <span className="font-medium">{m.nom}</span>
                                      {m.type && <span className="text-xs text-muted-foreground ml-2">({m.type})</span>}
                                    </div>
                                    {m.marque && <span className="text-xs text-muted-foreground">{m.marque}</span>}
                                  </div>
                                ))
                              }
                              {materiels.filter(m => {
                                const query = (selection.searchQuery || '').toLowerCase();
                                if (!query) return true;
                                return m.nom?.toLowerCase().includes(query) || m.type?.toLowerCase().includes(query);
                              }).length === 0 && (
                                <p className="px-3 py-2 text-sm text-muted-foreground">Aucun matériel trouvé pour "{selection.searchQuery}"</p>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Bouton supprimer */}
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          materiels_selection: (prev.materiels_selection || []).filter((_, i) => i !== idx)
                        }));
                      }}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Résumé des matériels sélectionnés */}
            {(formData.materiels_selection || []).filter(s => s.id).length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {(formData.materiels_selection || []).filter(s => s.id).map((s, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    <Tractor className="w-3 h-3 mr-1" />
                    {s.nom}
                    {s.type && <span className="opacity-70 ml-1">({s.type})</span>}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Section Traitement Phyto - Conditionnelle */}
          {(formData.type_operation === 'traitement' || formData.operation?.toLowerCase().includes('traitement') || formData.operation?.toLowerCase().includes('phyto')) && (
            <div className="space-y-3 border-t pt-4 bg-blue-50/50 dark:bg-blue-950/20 -mx-6 px-6 py-4">
              <Label className="text-base font-semibold flex items-center gap-2 text-blue-700 dark:text-blue-300">
                <Droplet className="w-4 h-4" />
                Paramètres de pulvérisation
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Volume bouillie (L/ha) *</Label>
                  <Input
                    type="number"
                    value={formData.volume_bouillie_ha}
                    onChange={(e) => setFormData(prev => ({ ...prev, volume_bouillie_ha: e.target.value }))}
                    placeholder="Ex: 200"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Pression (bars)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={formData.pression_bars}
                    onChange={(e) => setFormData(prev => ({ ...prev, pression_bars: e.target.value }))}
                    placeholder="Ex: 3"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Vitesse (km/h)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={formData.vitesse_avancement}
                    onChange={(e) => setFormData(prev => ({ ...prev, vitesse_avancement: e.target.value }))}
                    placeholder="Ex: 6"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Type de buse</Label>
                  <Select 
                    value={formData.type_buse}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, type_buse: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Choisir" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fente_110">Fente 110°</SelectItem>
                      <SelectItem value="fente_80">Fente 80°</SelectItem>
                      <SelectItem value="miroir">Miroir</SelectItem>
                      <SelectItem value="anti_derive">Anti-dérive</SelectItem>
                      <SelectItem value="injection">Injection air</SelectItem>
                      <SelectItem value="centrifuge">Centrifuge</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Alerte si volume non renseigné */}
              {!formData.volume_bouillie_ha && (
                <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-100 dark:bg-amber-900/30 px-3 py-2 rounded">
                  <AlertCircle className="w-4 h-4" />
                  Le volume de bouillie/ha est requis pour le registre phytosanitaire
                </div>
              )}
            </div>
          )}

          {/* Section Fertilisation - Conditionnelle */}
          {(formData.type_operation === 'fertilisation' || formData.operation?.toLowerCase().includes('fertil') || formData.operation?.toLowerCase().includes('engrais') || formData.operation?.toLowerCase().includes('apport')) && (
            <div className="space-y-4 border-t pt-4 bg-green-50/50 dark:bg-green-950/20 -mx-6 px-6 py-4">
              <div className="flex justify-between items-center">
                <Label className="text-base font-semibold flex items-center gap-2 text-green-700 dark:text-green-300">
                  <Leaf className="w-4 h-4" />
                  Cahier de fertilisation
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2 text-green-700 border-green-300 hover:bg-green-100"
                  onClick={() => window.open('/fertilisation', '_blank')}
                >
                  <Sparkles className="w-4 h-4" />
                  Suggestion IA
                </Button>
              </div>
              
              {/* Type d'apport */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Type d'apport</Label>
                  <Select 
                    value={formData.fertilisation?.type_apport || ''}
                    onValueChange={(v) => setFormData(prev => ({ 
                      ...prev, 
                      fertilisation: { ...prev.fertilisation, type_apport: v }
                    }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Choisir" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mineral">Minéral</SelectItem>
                      <SelectItem value="organique">Organique</SelectItem>
                      <SelectItem value="mixte">Mixte</SelectItem>
                      <SelectItem value="fertigation">Fertigation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Forme d'azote</Label>
                  <Select 
                    value={formData.fertilisation?.forme_azote || ''}
                    onValueChange={(v) => setFormData(prev => ({ 
                      ...prev, 
                      fertilisation: { ...prev.fertilisation, forme_azote: v }
                    }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Choisir" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ammonitrate">Ammonitrate (33.5%)</SelectItem>
                      <SelectItem value="uree">Urée (46%)</SelectItem>
                      <SelectItem value="solution_azotee">Solution azotée (39%)</SelectItem>
                      <SelectItem value="sulfate_ammoniaque">Sulfate d'ammoniaque</SelectItem>
                      <SelectItem value="organique">Organique</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Méthode d'épandage</Label>
                  <Select 
                    value={formData.fertilisation?.methode_epandage || ''}
                    onValueChange={(v) => setFormData(prev => ({ 
                      ...prev, 
                      fertilisation: { ...prev.fertilisation, methode_epandage: v }
                    }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Choisir" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="centrifuge">Centrifuge</SelectItem>
                      <SelectItem value="rampe">Rampe (localisé)</SelectItem>
                      <SelectItem value="enfouisseur">Enfouisseur</SelectItem>
                      <SelectItem value="fertigation">Fertigation</SelectItem>
                      <SelectItem value="manuel">Manuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-xs">Enfouissement</Label>
                    <div className="flex items-center gap-2 h-9">
                      <Switch
                        checked={formData.fertilisation?.enfouissement || false}
                        onCheckedChange={(v) => setFormData(prev => ({ 
                          ...prev, 
                          fertilisation: { ...prev.fertilisation, enfouissement: v }
                        }))}
                      />
                      <span className="text-xs">{formData.fertilisation?.enfouissement ? 'Oui' : 'Non'}</span>
                    </div>
                  </div>
                  {formData.fertilisation?.enfouissement && (
                    <div className="flex-1">
                      <Label className="text-xs">Délai (h)</Label>
                      <Input
                        type="number"
                        className="h-9"
                        placeholder="24"
                        value={formData.fertilisation?.delai_enfouissement_h || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          fertilisation: { ...prev.fertilisation, delai_enfouissement_h: e.target.value }
                        }))}
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Éléments fertilisants - Bilan */}
              <div className="border-t pt-3">
                <Label className="text-xs font-semibold mb-2 block">Éléments fertilisants (kg/ha)</Label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[10px] text-blue-600">N total</Label>
                    <Input
                      type="number"
                      className="h-8 text-xs"
                      placeholder="0"
                      value={formData.fertilisation?.azote_total_kg || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        fertilisation: { ...prev.fertilisation, azote_total_kg: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-blue-600">N efficace</Label>
                    <Input
                      type="number"
                      className="h-8 text-xs"
                      placeholder="0"
                      value={formData.fertilisation?.azote_efficace_kg || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        fertilisation: { ...prev.fertilisation, azote_efficace_kg: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-orange-600">P₂O₅</Label>
                    <Input
                      type="number"
                      className="h-8 text-xs"
                      placeholder="0"
                      value={formData.fertilisation?.phosphore_p2o5_kg || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        fertilisation: { ...prev.fertilisation, phosphore_p2o5_kg: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-purple-600">K₂O</Label>
                    <Input
                      type="number"
                      className="h-8 text-xs"
                      placeholder="0"
                      value={formData.fertilisation?.potasse_k2o_kg || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        fertilisation: { ...prev.fertilisation, potasse_k2o_kg: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-green-600">MgO</Label>
                    <Input
                      type="number"
                      className="h-8 text-xs"
                      placeholder="0"
                      value={formData.fertilisation?.magnesie_mgo_kg || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        fertilisation: { ...prev.fertilisation, magnesie_mgo_kg: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-yellow-600">SO₃</Label>
                    <Input
                      type="number"
                      className="h-8 text-xs"
                      placeholder="0"
                      value={formData.fertilisation?.soufre_so3_kg || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        fertilisation: { ...prev.fertilisation, soufre_so3_kg: e.target.value }
                      }))}
                    />
                  </div>
                </div>
              </div>
              
              {/* Données pour bilan azoté */}
              <div className="border-t pt-3">
                <Label className="text-xs font-semibold mb-2 block">Données pour bilan prévisionnel</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[10px]">Objectif rendement (q/ha)</Label>
                    <Input
                      type="number"
                      className="h-8 text-xs"
                      placeholder="Ex: 80"
                      value={formData.fertilisation?.objectif_rendement || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        fertilisation: { ...prev.fertilisation, objectif_rendement: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px]">Reliquat sortie hiver (kg N/ha)</Label>
                    <Input
                      type="number"
                      className="h-8 text-xs"
                      placeholder="Ex: 40"
                      value={formData.fertilisation?.reliquat_sortie_hiver || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        fertilisation: { ...prev.fertilisation, reliquat_sortie_hiver: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px]">Minéralisation humus (kg N/ha)</Label>
                    <Input
                      type="number"
                      className="h-8 text-xs"
                      placeholder="Ex: 30"
                      value={formData.fertilisation?.mineralisation_humus || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        fertilisation: { ...prev.fertilisation, mineralisation_humus: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px]">Apport précédent cultural (kg N/ha)</Label>
                    <Input
                      type="number"
                      className="h-8 text-xs"
                      placeholder="Ex: 20"
                      value={formData.fertilisation?.apport_precedent || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        fertilisation: { ...prev.fertilisation, apport_precedent: e.target.value }
                      }))}
                    />
                  </div>
                </div>
              </div>
              
              {/* Matière organique */}
              {formData.fertilisation?.type_apport === 'organique' || formData.fertilisation?.type_apport === 'mixte' ? (
                <div className="border-t pt-3">
                  <Label className="text-xs font-semibold mb-2 block">Apport organique</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px]">Quantité MO (t/ha)</Label>
                      <Input
                        type="number"
                        className="h-8 text-xs"
                        placeholder="Ex: 20"
                        value={formData.fertilisation?.matiere_organique_t || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          fertilisation: { ...prev.fertilisation, matiere_organique_t: e.target.value }
                        }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px]">Rapport C/N</Label>
                      <Input
                        type="number"
                        className="h-8 text-xs"
                        placeholder="Ex: 15"
                        value={formData.fertilisation?.c_n_ratio || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          fertilisation: { ...prev.fertilisation, c_n_ratio: e.target.value }
                        }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px]">Oligo-éléments</Label>
                      <Input
                        className="h-8 text-xs"
                        placeholder="B, Mn, Zn..."
                        value={formData.fertilisation?.oligo_elements || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          fertilisation: { ...prev.fertilisation, oligo_elements: e.target.value }
                        }))}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
              
              {/* Résumé bilan avec calcul poids total */}
              {(formData.fertilisation?.azote_total_kg || formData.fertilisation?.phosphore_p2o5_kg || formData.fertilisation?.potasse_k2o_kg) && formData.surface_travaillee && (
                <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border space-y-3">
                  <Label className="text-xs font-semibold mb-2 block">📊 Résumé de l'apport</Label>
                  <div className="flex flex-wrap gap-3 text-xs">
                    {formData.fertilisation?.azote_total_kg && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        N: {formData.fertilisation.azote_total_kg} kg/ha
                      </Badge>
                    )}
                    {formData.fertilisation?.phosphore_p2o5_kg && (
                      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                        P₂O₅: {formData.fertilisation.phosphore_p2o5_kg} kg/ha
                      </Badge>
                    )}
                    {formData.fertilisation?.potasse_k2o_kg && (
                      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                        K₂O: {formData.fertilisation.potasse_k2o_kg} kg/ha
                      </Badge>
                    )}
                  </div>
                  
                  {/* Calcul quantités totales pour commande */}
                  <div className="border-t pt-2 mt-2">
                    <Label className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                      🚚 Quantités totales à commander ({formData.surface_travaillee} ha)
                    </Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm">
                      {formData.fertilisation?.azote_total_kg && (
                        <div className="bg-blue-100 rounded p-2 text-center">
                          <div className="font-bold text-blue-800">
                            {Math.round(parseFloat(formData.fertilisation.azote_total_kg) * parseFloat(formData.surface_travaillee))} kg
                          </div>
                          <div className="text-xs text-blue-600">Azote total</div>
                        </div>
                      )}
                      {formData.fertilisation?.phosphore_p2o5_kg && (
                        <div className="bg-orange-100 rounded p-2 text-center">
                          <div className="font-bold text-orange-800">
                            {Math.round(parseFloat(formData.fertilisation.phosphore_p2o5_kg) * parseFloat(formData.surface_travaillee))} kg
                          </div>
                          <div className="text-xs text-orange-600">P₂O₅ total</div>
                        </div>
                      )}
                      {formData.fertilisation?.potasse_k2o_kg && (
                        <div className="bg-purple-100 rounded p-2 text-center">
                          <div className="font-bold text-purple-800">
                            {Math.round(parseFloat(formData.fertilisation.potasse_k2o_kg) * parseFloat(formData.surface_travaillee))} kg
                          </div>
                          <div className="text-xs text-purple-600">K₂O total</div>
                        </div>
                      )}
                      {formData.fertilisation?.matiere_organique_t && (
                        <div className="bg-green-100 rounded p-2 text-center">
                          <div className="font-bold text-green-800">
                            {(parseFloat(formData.fertilisation.matiere_organique_t) * parseFloat(formData.surface_travaillee)).toFixed(1)} t
                          </div>
                          <div className="text-xs text-green-600">Matière organique</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Météo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Météo</Label>
              <Select 
                value={formData.meteo}
                onValueChange={(v) => setFormData(prev => ({ ...prev, meteo: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Conditions météo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ensoleille">
                    <div className="flex items-center gap-2">
                      <Sun className="w-4 h-4 text-yellow-500" />
                      Ensoleillé
                    </div>
                  </SelectItem>
                  <SelectItem value="nuageux">
                    <div className="flex items-center gap-2">
                      <Cloud className="w-4 h-4 text-gray-500" />
                      Nuageux
                    </div>
                  </SelectItem>
                  <SelectItem value="pluie">
                    <div className="flex items-center gap-2">
                      <CloudRain className="w-4 h-4 text-blue-500" />
                      Pluie
                    </div>
                  </SelectItem>
                  <SelectItem value="vent">
                    <div className="flex items-center gap-2">
                      <Wind className="w-4 h-4 text-teal-500" />
                      Vent
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Température (°C)</Label>
              <Input
                type="number"
                value={formData.temperature}
                onChange={(e) => setFormData(prev => ({ ...prev, temperature: e.target.value }))}
                placeholder="Ex: 18"
              />
            </div>
            <div className="space-y-2">
              <Label>Vent (km/h)</Label>
              <Input
                type="number"
                value={formData.vent_kmh}
                onChange={(e) => setFormData(prev => ({ ...prev, vent_kmh: e.target.value }))}
                placeholder="Ex: 10"
              />
              {formData.vent_kmh && parseFloat(formData.vent_kmh) > 19 && (
                <p className="text-[10px] text-amber-600">⚠️ Vent fort - traitement déconseillé</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Hygrométrie (%)</Label>
              <Input
                type="number"
                value={formData.hygrometrie}
                onChange={(e) => setFormData(prev => ({ ...prev, hygrometrie: e.target.value }))}
                placeholder="Ex: 65"
              />
            </div>
          </div>

          {/* Commentaire */}
          <div className="space-y-2">
            <Label>Commentaire / Observations</Label>
            <Textarea
              value={formData.commentaire}
              onChange={(e) => setFormData(prev => ({ ...prev, commentaire: e.target.value }))}
              placeholder="Notes sur l'intervention..."
              rows={3}
              data-testid="input-commentaire"
            />
          </div>

          {/* Section Notifications & Signature */}
          <div className="space-y-4 border-t pt-4">
            <Label className="text-lg font-semibold flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notifications & Signature
            </Label>
            
            {/* Durée prévisionnelle */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Durée prévisionnelle</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.5"
                    value={formData.duree_previsionnelle}
                    onChange={(e) => setFormData(prev => ({ ...prev, duree_previsionnelle: e.target.value }))}
                    placeholder="Ex: 3"
                    className="flex-1"
                  />
                  <span className="text-sm text-muted-foreground self-center">heures</span>
                </div>
                <p className="text-xs text-muted-foreground">Estimation pour planification</p>
              </div>
            </div>
            
            {/* Notification opérateur */}
            <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
              <Switch
                id="notifier-operateur"
                checked={formData.notifier_operateur}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, notifier_operateur: checked }))}
                data-testid="switch-notifier-operateur"
              />
              <div className="space-y-1">
                <Label htmlFor="notifier-operateur" className="cursor-pointer font-medium">
                  Notifier l'opérateur
                </Label>
                <p className="text-xs text-muted-foreground">
                  L'opérateur recevra un email et une notification push avec les détails de l'intervention
                </p>
              </div>
            </div>
            
            {/* Notification client */}
            <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-start space-x-3">
                <Switch
                  id="notifier-client"
                  checked={formData.notifier_client}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, notifier_client: checked }))}
                  data-testid="switch-notifier-client"
                />
                <div className="space-y-1 flex-1">
                  <Label htmlFor="notifier-client" className="cursor-pointer font-medium">
                    Prévenir le client
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Envoyer un email au client pour l'informer de l'intervention planifiée
                  </p>
                </div>
              </div>
              
              {formData.notifier_client && (
                <div className="ml-10 space-y-2">
                  <Label className="text-sm">Email du client</Label>
                  <Input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                    placeholder="client@example.com"
                    data-testid="input-client-email"
                  />
                </div>
              )}
            </div>
            
            {/* Signature électronique */}
            <div className="space-y-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-start space-x-3">
                <Switch
                  id="demander-signature"
                  checked={formData.demander_signature}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, demander_signature: checked }))}
                  data-testid="switch-demander-signature"
                />
                <div className="space-y-1 flex-1">
                  <Label htmlFor="demander-signature" className="cursor-pointer font-medium flex items-center gap-2">
                    <FileSignature className="w-4 h-4 text-primary" />
                    Demander une signature de réception
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Obtenir une signature du client pour confirmer la réalisation du chantier
                  </p>
                </div>
              </div>
              
              {formData.demander_signature && (
                <div className="ml-10 space-y-3">
                  <Label className="text-sm font-medium">Type de signature</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type_signature: 'email' }))}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        formData.type_signature === 'email'
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                      data-testid="signature-type-email"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Mail className="w-4 h-4" />
                        <span className="font-medium text-sm">Par email</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Le client reçoit un lien par email pour signer
                      </p>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type_signature: 'direct' }))}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        formData.type_signature === 'direct'
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                      data-testid="signature-type-direct"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Smartphone className="w-4 h-4" />
                        <span className="font-medium text-sm">Sur appareil</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Signature directe sur téléphone ou tablette
                      </p>
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    L'opérateur pourra demander la signature via l'assistant
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Section Transport / Livraison / Chargement */}
          {(formData.operation?.toLowerCase().includes('transport') || 
            formData.operation?.toLowerCase().includes('livraison') || 
            formData.operation?.toLowerCase().includes('chargement') ||
            formData.operation?.toLowerCase().includes('trajet') ||
            formData.operation?.toLowerCase().includes('préparation commande') ||
            formData.operation?.toLowerCase().includes('preparation commande')) && (
            <div className="space-y-4 border-t pt-4 bg-orange-50/50 dark:bg-orange-950/20 -mx-6 px-6 py-4">
              <Label className="text-base font-semibold flex items-center gap-2 text-orange-700 dark:text-orange-300">
                <Truck className="w-5 h-5" />
                Transport & Documents
              </Label>
              
              {/* Lieux */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Lieu de départ</Label>
                  <Input
                    value={formData.transport?.lieu_depart || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      transport: { ...prev.transport, lieu_depart: e.target.value }
                    }))}
                    placeholder="Ex: Dépôt, Hangar..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Lieu d'arrivée</Label>
                  <Input
                    value={formData.transport?.lieu_arrivee || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      transport: { ...prev.transport, lieu_arrivee: e.target.value }
                    }))}
                    placeholder="Ex: Chez client X..."
                  />
                </div>
              </div>
              
              {/* Marchandise & Quantité */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Marchandise transportée</Label>
                  <Input
                    value={formData.transport?.marchandise || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      transport: { ...prev.transport, marchandise: e.target.value }
                    }))}
                    placeholder="Ex: Blé, Raisin, Matériel..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Quantité</Label>
                  <Input
                    value={formData.transport?.quantite || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      transport: { ...prev.transport, quantite: e.target.value }
                    }))}
                    placeholder="Ex: 10 tonnes, 500 caisses..."
                  />
                </div>
              </div>
              
              {/* Véhicule & Chauffeur */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Véhicule</Label>
                  <Input
                    value={formData.transport?.vehicule || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      transport: { ...prev.transport, vehicule: e.target.value }
                    }))}
                    placeholder="Ex: Camion 44T, Tracteur + benne..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Chauffeur</Label>
                  <Input
                    value={formData.transport?.chauffeur || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      transport: { ...prev.transport, chauffeur: e.target.value }
                    }))}
                    placeholder="Nom du chauffeur"
                  />
                </div>
              </div>
              
              {/* Documents à générer */}
              <div className="space-y-3 mt-4">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Documents à générer
                </Label>
                
                <div className="grid grid-cols-2 gap-3">
                  {/* Bon de livraison */}
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.transport?.bon_livraison_requis 
                        ? 'border-green-500 bg-green-50 dark:bg-green-950/30' 
                        : 'border-dashed border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      transport: { 
                        ...prev.transport, 
                        bon_livraison_requis: !prev.transport?.bon_livraison_requis 
                      }
                    }))}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${formData.transport?.bon_livraison_requis ? 'bg-green-500 text-white' : 'bg-gray-100'}`}>
                        <ClipboardList className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium">Bon de Livraison (BL)</p>
                        <p className="text-xs text-muted-foreground">Accompagne la marchandise</p>
                      </div>
                      {formData.transport?.bon_livraison_requis && (
                        <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />
                      )}
                    </div>
                  </div>
                  
                  {/* Lettre de voiture */}
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.transport?.lettre_voiture_requise 
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' 
                        : 'border-dashed border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      transport: { 
                        ...prev.transport, 
                        lettre_voiture_requise: !prev.transport?.lettre_voiture_requise 
                      }
                    }))}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${formData.transport?.lettre_voiture_requise ? 'bg-blue-500 text-white' : 'bg-gray-100'}`}>
                        <Package className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium">Lettre de Voiture (CMR)</p>
                        <p className="text-xs text-muted-foreground">Transport routier réglementé</p>
                      </div>
                      {formData.transport?.lettre_voiture_requise && (
                        <CheckCircle2 className="w-5 h-5 text-blue-500 ml-auto" />
                      )}
                    </div>
                  </div>
                </div>
                
                {(formData.transport?.bon_livraison_requis || formData.transport?.lettre_voiture_requise) && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg text-amber-700 dark:text-amber-300 text-sm">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    <span>Les documents seront générés automatiquement et disponibles pour signature à la livraison.</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section Commande (pour conditionnement/emballage) */}
          {(formData.operation?.toLowerCase().includes('conditionnement') || 
            formData.operation?.toLowerCase().includes('emballage') ||
            formData.operation?.toLowerCase().includes('préparation commande') ||
            formData.operation?.toLowerCase().includes('preparation commande')) && (
            <div className="space-y-4 border-t pt-4 bg-purple-50/50 dark:bg-purple-950/20 -mx-6 px-6 py-4">
              <Label className="text-base font-semibold flex items-center gap-2 text-purple-700 dark:text-purple-300">
                <Package className="w-5 h-5" />
                Commande à préparer
              </Label>
              
              {commandes.length > 0 ? (
                <div className="space-y-3">
                  <Select
                    value={formData.commande_id || ''}
                    onValueChange={(value) => setFormData(prev => ({ 
                      ...prev, 
                      commande_id: value,
                      commande: commandes.find(c => c.id === value)
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une commande..." />
                    </SelectTrigger>
                    <SelectContent>
                      {commandes.map((cmd) => (
                        <SelectItem key={cmd.id} value={cmd.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{cmd.numero || cmd.reference || `Commande #${cmd.id?.slice(0,8)}`}</span>
                            {cmd.client_nom && <span className="text-muted-foreground">- {cmd.client_nom}</span>}
                            {cmd.date && <span className="text-xs text-muted-foreground ml-2">{new Date(cmd.date).toLocaleDateString('fr-FR')}</span>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {formData.commande && (
                    <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-lg">{formData.commande.numero || formData.commande.reference}</span>
                        <Badge variant={formData.commande.statut === 'en_cours' ? 'default' : 'secondary'}>
                          {formData.commande.statut || 'En attente'}
                        </Badge>
                      </div>
                      {formData.commande.client_nom && (
                        <p className="text-sm text-muted-foreground">Client: {formData.commande.client_nom}</p>
                      )}
                      {formData.commande.lignes && formData.commande.lignes.length > 0 && (
                        <div className="mt-2 pt-2 border-t">
                          <p className="text-xs font-medium mb-1">Produits à préparer:</p>
                          <ul className="text-sm space-y-1">
                            {formData.commande.lignes.slice(0, 5).map((ligne, idx) => (
                              <li key={idx} className="flex justify-between">
                                <span>{ligne.produit || ligne.designation}</span>
                                <span className="text-muted-foreground">{ligne.quantite} {ligne.unite || 'pcs'}</span>
                              </li>
                            ))}
                            {formData.commande.lignes.length > 5 && (
                              <li className="text-muted-foreground">... et {formData.commande.lignes.length - 5} autres articles</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4 bg-muted/50 rounded">
                  Aucune commande disponible. Créez d'abord des commandes dans le module CRM.
                </p>
              )}
            </div>
          )}

          {/* Section Cibles (pour traitement phyto/fertilisation) */}
          {(formData.type_operation === 'traitement' || formData.type_operation === 'fertilisation') && (
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-lg font-semibold flex items-center gap-2">
                    🎯 Cibles de traitement
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ajoutez les cibles (ravageurs, maladies, adventices) pour obtenir des suggestions de produits
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    cibles: [...(prev.cibles || []), { id: Date.now(), nom: '', type: 'ravageur' }]
                  }))}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter cible
                </Button>
              </div>
              
              {/* Liste des cibles */}
              {(formData.cibles || []).length === 0 ? (
                <div className="text-center py-4 bg-muted/30 rounded-lg border-2 border-dashed">
                  <AlertCircle className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Aucune cible définie - Ajoutez une cible pour voir les suggestions de produits
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {(formData.cibles || []).map((cible, idx) => (
                    <div key={cible.id} className="flex items-center gap-2">
                      <Select
                        value={cible.type}
                        onValueChange={(v) => {
                          const updated = [...formData.cibles];
                          updated[idx].type = v;
                          setFormData(prev => ({ ...prev, cibles: updated }));
                        }}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ravageur">🐛 Ravageur</SelectItem>
                          <SelectItem value="maladie">🦠 Maladie</SelectItem>
                          <SelectItem value="adventice">🌿 Adventice</SelectItem>
                          <SelectItem value="carence">⚠️ Carence</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        className="flex-1"
                        placeholder={cible.type === 'ravageur' ? 'Ex: Mildiou, Oïdium, Puceron...' : 
                                    cible.type === 'maladie' ? 'Ex: Black rot, Botrytis...' :
                                    cible.type === 'adventice' ? 'Ex: Chiendent, Liseron...' :
                                    'Ex: Azote, Fer, Magnésium...'}
                        value={cible.nom}
                        onChange={(e) => {
                          const updated = [...formData.cibles];
                          updated[idx].nom = e.target.value;
                          setFormData(prev => ({ ...prev, cibles: updated }));
                        }}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            cibles: prev.cibles.filter((_, i) => i !== idx)
                          }));
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Suggestions IA de produits basées sur les cibles */}
              {(formData.cibles || []).length > 0 && formData.cibles.some(c => c.nom?.length >= 2) && (
                <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">💡</span>
                      <span className="font-semibold text-green-800 dark:text-green-200">Suggestions IA</span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={loadingSuggestions}
                      onClick={async () => {
                        setLoadingSuggestions(true);
                        try {
                          const parcelle = parcelles.find(p => p.id === formData.parcelle_id);
                          const culture = parcelle?.culture_actuelle || parcelle?.culture || '';
                          
                          // Vérifier si la parcelle est en BIO
                          const is_bio = parcelle?.is_bio || 
                                        parcelle?.mode_culture === 'bio' || 
                                        parcelle?.mode_culture === 'conversion' ||
                                        false;
                          
                          // Calculer les jours avant récolte si date de récolte prévue
                          let jours_avant_recolte = null;
                          if (parcelle?.date_recolte_prevue) {
                            const dateRecolte = new Date(parcelle.date_recolte_prevue);
                            const today = new Date();
                            jours_avant_recolte = Math.ceil((dateRecolte - today) / (1000 * 60 * 60 * 24));
                            if (jours_avant_recolte < 0) jours_avant_recolte = null;
                          }
                          
                          const token = localStorage.getItem('token');
                          const res = await fetch(`${API_URL}/api/registre-phyto/suggestions`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                              culture,
                              cibles: formData.cibles.filter(c => c.nom),
                              type_traitement: formData.type_operation,
                              is_bio: is_bio,
                              jours_avant_recolte: jours_avant_recolte,
                              entree_ouvriers_prevue: false // Peut être activé si besoin
                            })
                          });
                          
                          if (res.ok) {
                            const data = await res.json();
                            setFormData(prev => ({ ...prev, suggestions_produits: data.suggestions || [] }));
                            // Afficher message de priorité ou mode bio
                            if (data.message) {
                              if (data.priorite_casia) {
                                toast.success(data.message, { duration: 5000 });
                              } else {
                                toast.info(data.message);
                              }
                            }
                            // Afficher info CASIA si applicable
                            if (data.info_casia) {
                              toast.info(data.info_casia, { duration: 4000 });
                            }
                          }
                        } catch (err) {
                          console.error('Erreur suggestions:', err);
                        }
                        setLoadingSuggestions(false);
                      }}
                    >
                      {loadingSuggestions ? (
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                      ) : (
                        <Search className="w-4 h-4 mr-2" />
                      )}
                      Rechercher produits
                    </Button>
                  </div>
                  
                  {/* Indicateur mode BIO */}
                  {(() => {
                    const parcelle = parcelles.find(p => p.id === formData.parcelle_id);
                    const isBio = parcelle?.is_bio || parcelle?.mode_culture === 'bio' || parcelle?.mode_culture === 'conversion';
                    return isBio ? (
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded border border-green-300 mb-2">
                        <p className="text-sm text-green-800 dark:text-green-200 flex items-center gap-2">
                          <span>🌿</span>
                          <span className="font-medium">Parcelle en Agriculture Biologique</span>
                          <span className="text-xs">- Seuls les produits autorisés en BIO sont proposés</span>
                        </p>
                      </div>
                    ) : null;
                  })()}
                  
                  <p className="text-xs text-green-700 dark:text-green-300 mb-3">
                    Basé sur : {formData.cibles.filter(c => c.nom).map(c => c.nom).join(', ')} 
                    {(parcelles.find(p => p.id === formData.parcelle_id)?.culture_actuelle ||
                      parcelles.find(p => p.id === formData.parcelle_id)?.culture ||
                      parcelles.find(p => p.id === formData.parcelle_id)?.culture_nom) && 
                      ` • Culture: ${parcelles.find(p => p.id === formData.parcelle_id)?.culture_actuelle || parcelles.find(p => p.id === formData.parcelle_id)?.culture || parcelles.find(p => p.id === formData.parcelle_id)?.culture_nom}`}
                  </p>
                  
                  {(formData.suggestions_produits || []).length > 0 ? (
                    <div className="space-y-2">
                      {formData.suggestions_produits.map((sugg, idx) => (
                        <div 
                          key={idx} 
                          className={`p-2 rounded-md border cursor-pointer hover:bg-white/50 dark:hover:bg-black/20 transition-colors ${
                            sugg.homologue === false 
                              ? 'bg-red-50 dark:bg-red-900/20 border-red-300' 
                              : sugg.priorite_absolue
                                ? 'bg-emerald-200/70 dark:bg-emerald-900/40 border-emerald-500 ring-2 ring-emerald-400'
                                : sugg.is_casia 
                                  ? 'bg-emerald-100/50 dark:bg-emerald-900/20 border-emerald-300' 
                                  : 'bg-white/30 border-gray-200'
                          }`}
                          onClick={() => {
                            // Ajouter le produit suggéré à la liste des produits
                            setFormData(prev => ({
                              ...prev,
                              produits: [...(prev.produits || []), {
                                id: Date.now(),
                                type: 'phyto',
                                nom: sugg.nom,
                                numero_amm: sugg.numero_amm,
                                firme: sugg.firme,
                                quantite: sugg.dose_recommandee || '',
                                unite: sugg.unite || 'L/ha',
                                dose_max: sugg.dose_max,
                                dose_recommandee: sugg.dose_recommandee,
                                is_casia: sugg.is_casia,
                                is_pnpp: sugg.is_pnpp,
                                dar: sugg.dar,
                                dre: sugg.dre,
                                homologue: sugg.homologue
                              }]
                            }));
                            toast.success(`${sugg.nom} ajouté aux produits`);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-wrap">
                              {sugg.priorite_absolue && (
                                <Badge className="bg-emerald-700 text-white text-xs animate-pulse">⭐ PRIORITÉ</Badge>
                              )}
                              {sugg.is_casia && (
                                <Badge className="bg-emerald-600 text-white text-xs">CASIA</Badge>
                              )}
                              {sugg.is_pnpp && (
                                <Badge className="bg-teal-600 text-white text-xs">PNPP</Badge>
                              )}
                              {sugg.is_bio && (
                                <Badge className="bg-green-700 text-white text-xs">🌿 BIO</Badge>
                              )}
                              {sugg.dar === 0 && (
                                <Badge className="bg-blue-600 text-white text-xs">DAR 0</Badge>
                              )}
                              {sugg.dre === 0 && (
                                <Badge className="bg-purple-600 text-white text-xs">DRE 0</Badge>
                              )}
                              {sugg.homologue === true && !sugg.is_casia && (
                                <Badge className="bg-green-600 text-white text-xs">✓ Homologué</Badge>
                              )}
                              {sugg.homologue === false && (
                                <Badge className="bg-red-600 text-white text-xs">⚠ Non homologué</Badge>
                              )}
                              <span className="font-medium text-sm">{sugg.nom}</span>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {sugg.dose_recommandee} {sugg.unite || 'L/ha'}
                            </Badge>
                          </div>
                          {/* Message avantage CASIA */}
                          {sugg.message_avantage && (
                            <p className="text-xs text-emerald-700 dark:text-emerald-300 font-medium mt-1 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-1 rounded">
                              {sugg.message_avantage}
                            </p>
                          )}
                          <div className="flex items-center justify-between mt-1">
                            {sugg.firme && (
                              <p className="text-xs text-muted-foreground">{sugg.firme} • AMM: {sugg.numero_amm}</p>
                            )}
                            {sugg.homologation_raison && (
                              <p className={`text-xs ${sugg.homologue === false ? 'text-red-600 font-medium' : 'text-green-600'}`}>
                                {sugg.homologation_raison}
                              </p>
                            )}
                          </div>
                          {/* Info DAR/DRE pour produits non-CASIA */}
                          {!sugg.is_casia && (sugg.dar > 0 || sugg.dre > 0) && (
                            <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                              ⚠️ DAR: {sugg.dar}j • DRE: {sugg.dre}h
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      Cliquez sur "Rechercher produits" pour obtenir des suggestions
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Section Intrants / Phytos / Divers */}
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center justify-between">
              <Label className="text-lg font-semibold">Produits utilisés (EPHY/CASIA)</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  produits: [...(prev.produits || []), {
                    id: Date.now(),
                    type: 'phyto',
                    nom: '',
                    fournisseur: '',
                    quantite: '',
                    unite: 'L/ha',
                    cout_unitaire: '',
                    cout_total: '',
                    // Données EPHY
                    numero_amm: '',
                    firme: '',
                    dose_max: null,
                    dose_recommandee: null,
                    cultures_autorisees: [],
                    contient_cuivre: false,
                    cuivre_kg_ha: null,
                    alerte: null
                  }]
                }))}
              >
                <Plus className="w-4 h-4 mr-1" />
                Ajouter produit
              </Button>
            </div>
            
            {(formData.produits || []).length === 0 ? (
              <div className="text-center py-6 bg-muted/50 rounded-lg border-2 border-dashed">
                <Leaf className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">
                  Aucun produit ajouté
                </p>
                <p className="text-xs text-muted-foreground">
                  Recherchez dans la base officielle EPHY/CASIA pour des recommandations de doses
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {(formData.produits || []).map((produit, idx) => (
                  <Card key={produit.id} className={`p-3 ${produit.alerte ? 'border-amber-500 bg-amber-50/50 dark:bg-amber-950/20' : ''}`}>
                    {/* Alertes IA */}
                    {produit.alerte && (
                      <div className={`mb-3 p-2 rounded-md text-xs flex items-start gap-2 ${
                        produit.alerte.type === 'danger' ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200' :
                        produit.alerte.type === 'warning' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200' :
                        'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200'
                      }`}>
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold">{produit.alerte.titre}</p>
                          <p>{produit.alerte.message}</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                      {/* Type */}
                      <div className="col-span-2 md:col-span-1">
                        <Label className="text-xs">Type</Label>
                        <Select
                          value={produit.type}
                          onValueChange={(v) => {
                            const updated = [...formData.produits];
                            updated[idx].type = v;
                            setFormData(prev => ({ ...prev, produits: updated }));
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="intrant">🌱 Intrant</SelectItem>
                            <SelectItem value="herbicide">🌿 Herbicide</SelectItem>
                            <SelectItem value="fongicide">🍄 Fongicide</SelectItem>
                            <SelectItem value="insecticide">🐛 Insecticide</SelectItem>
                            <SelectItem value="engrais_foliaire">🍃 Engrais foliaire</SelectItem>
                            <SelectItem value="phyto">🧪 Autre Phyto</SelectItem>
                            <SelectItem value="engrais">💧 Engrais sol</SelectItem>
                            <SelectItem value="semence">🌾 Semence</SelectItem>
                            <SelectItem value="divers">📦 Divers</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Nom produit avec recherche EPHY/CASIA */}
                      <div className="col-span-2 md:col-span-2 relative">
                        <Label className="text-xs">Nom du produit (EPHY/CASIA)</Label>
                        <div className="relative">
                          <Input
                            className={`h-8 text-xs pr-8 ${!produit.numero_amm && produit.nom?.length > 3 ? 'border-amber-400' : ''}`}
                            placeholder="Tapez pour rechercher dans EPHY..."
                            value={produit.nom}
                            onChange={async (e) => {
                              const value = e.target.value;
                              
                              // Mise à jour immédiate du nom
                              setFormData(prev => {
                                const updated = [...prev.produits];
                                updated[idx] = {
                                  ...updated[idx],
                                  nom: value,
                                  showSuggestions: true,
                                  searching: value.length >= 2,
                                  suggestions: value.length < 2 ? [] : updated[idx].suggestions // Garder les suggestions existantes pendant la recherche
                                };
                                return { ...prev, produits: updated };
                              });
                              
                              // Recherche EPHY/CASIA si plus de 2 caractères
                              if (value.length >= 2) {
                                try {
                                  const token = localStorage.getItem('token');
                                  const res = await fetch(`${API_URL}/api/registre-phyto/ephy/search?q=${encodeURIComponent(value)}&limit=10`, {
                                    headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  if (!res.ok) {
                                    throw new Error(`Erreur API: ${res.status}`);
                                  }
                                  const data = await res.json();
                                  setFormData(prev => {
                                    const updated = [...prev.produits];
                                    updated[idx] = {
                                      ...updated[idx],
                                      suggestions: data.produits || [],
                                      searching: false
                                    };
                                    return { ...prev, produits: updated };
                                  });
                                } catch (err) {
                                  console.log('Recherche EPHY:', err);
                                  setFormData(prev => {
                                    const updated = [...prev.produits];
                                    updated[idx] = { ...updated[idx], searching: false, suggestions: [] };
                                    return { ...prev, produits: updated };
                                  });
                                }
                              } else {
                                setFormData(prev => {
                                  const updated = [...prev.produits];
                                  updated[idx] = {
                                    ...updated[idx],
                                    suggestions: [],
                                    searching: false
                                  };
                                  return { ...prev, produits: updated };
                                });
                              }
                            }}
                            onFocus={() => {
                              setFormData(prev => {
                                const updated = [...prev.produits];
                                updated[idx] = { ...updated[idx], showSuggestions: true };
                                return { ...prev, produits: updated };
                              });
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                setFormData(prev => {
                                  const updated = [...prev.produits];
                                  updated[idx] = { ...updated[idx], showSuggestions: false };
                                  // Si produit saisi manuellement sans AMM, ajouter alerte
                                  if (updated[idx].nom?.length > 3 && !updated[idx].numero_amm) {
                                    updated[idx].alerte = {
                                      type: 'warning',
                                      titre: '⚠️ PRODUIT NON HOMOLOGUÉ / NON RÉFÉRENCÉ',
                                      message: `"${updated[idx].nom}" n'est pas trouvé dans la base EPHY. Vérifiez l'orthographe ou confirmez qu'il s'agit d'un produit hors catalogue officiel.`
                                    };
                                  }
                                  return { ...prev, produits: updated };
                                });
                              }, 300);
                            }}
                          />
                          {produit.searching && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2">
                              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            </div>
                          )}
                          
                          {/* Liste des suggestions EPHY */}
                          {produit.showSuggestions && Array.isArray(produit.suggestions) && produit.suggestions.length > 0 && (
                            <div className="absolute z-[300] w-full mt-1 bg-popover border rounded-md shadow-xl max-h-64 overflow-y-auto">
                              <div className="px-3 py-1.5 bg-muted/50 text-xs text-muted-foreground border-b sticky top-0 z-10 bg-background">
                                🔍 {produit.suggestions.length} produits trouvés dans EPHY
                              </div>
                              {produit.suggestions.map((suggestion, sIdx) => {
                                const contientCuivre = suggestion.nom?.toLowerCase().includes('cuivre') || 
                                                       suggestion.substances_actives?.toLowerCase().includes('cuivre');
                                
                                // Mapping des pictogrammes SGH vers emojis/images
                                const PICTO_LABELS = {
                                  'GHS01': { emoji: '💥', label: 'Explosif' },
                                  'GHS02': { emoji: '🔥', label: 'Inflammable' },
                                  'GHS03': { emoji: '⭕', label: 'Comburant' },
                                  'GHS04': { emoji: '🔵', label: 'Gaz sous pression' },
                                  'GHS05': { emoji: '⚗️', label: 'Corrosif' },
                                  'GHS06': { emoji: '☠️', label: 'Toxique' },
                                  'GHS07': { emoji: '⚠️', label: 'Attention' },
                                  'GHS08': { emoji: '🫁', label: 'Danger santé' },
                                  'GHS09': { emoji: '🐟', label: 'Environnement' },
                                };
                                
                                return (
                                  <div
                                    key={sIdx}
                                    className="px-3 py-2 hover:bg-accent cursor-pointer text-xs border-b last:border-0 transition-colors"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      setFormData(prev => {
                                        const updated = [...prev.produits];
                                        updated[idx] = {
                                          ...updated[idx],
                                          nom: suggestion.nom,
                                          numero_amm: suggestion.numero_amm,
                                          firme: suggestion.firme,
                                          type: suggestion.type_produit?.includes('bio') ? 'intrant' : 'phyto',
                                          showSuggestions: false,
                                          suggestions: [],
                                          contient_cuivre: contientCuivre,
                                          dose_max: suggestion.dose_max || (contientCuivre ? 4 : 10),
                                          dose_recommandee: suggestion.dose_max ? (suggestion.dose_max * 0.8).toFixed(1) : null,
                                          unite: suggestion.unite_dose || 'L/ha',
                                          substances_actives: suggestion.substances_actives,
                                          substances_liste: suggestion.substances_liste,
                                          cultures_autorisees: suggestion.cultures || [],
                                          formulation: suggestion.formulation,
                                          pictos_sgh: suggestion.pictos_sgh,
                                          url_fiche_ephy: suggestion.url_fiche_ephy,
                                          url_fds: suggestion.url_fds,
                                          alerte: contientCuivre ? {
                                            type: 'info',
                                            titre: '🟢 Produit à base de cuivre - Homologué',
                                            message: `AMM: ${suggestion.numero_amm}. ${suggestion.substances_actives || ''} - Limite UE: 4 kg Cu/ha/an. Dose max: ${suggestion.dose_max || 4} ${suggestion.unite_dose || 'L/ha'}`
                                          } : {
                                            type: 'info',
                                            titre: '✅ Produit homologué EPHY',
                                            message: `AMM: ${suggestion.numero_amm}${suggestion.firme ? ` - ${suggestion.firme}` : ''}${suggestion.substances_actives ? ` | ${suggestion.substances_actives}` : ''}`
                                          }
                                        };
                                        return { ...prev, produits: updated };
                                      });
                                    }}
                                  >
                                    {/* En-tête produit */}
                                    <div className="flex items-center justify-between">
                                      <span className="font-semibold text-sm">{suggestion.nom}</span>
                                      <div className="flex items-center gap-1">
                                        {/* Pictogrammes SGH */}
                                        {suggestion.pictos_sgh?.map((picto, pi) => (
                                          <span 
                                            key={pi} 
                                            title={PICTO_LABELS[picto]?.label || picto}
                                            className="text-base"
                                          >
                                            {PICTO_LABELS[picto]?.emoji || '⚠️'}
                                          </span>
                                        ))}
                                        {contientCuivre && (
                                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-orange-100 text-orange-700 border-orange-300">
                                            Cu
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Matière active - bien visible */}
                                    {suggestion.substances_actives && (
                                      <div className="bg-blue-50 dark:bg-blue-950/30 rounded px-2 py-1 mt-1">
                                        <p className="text-[11px] font-medium text-blue-700 dark:text-blue-300">
                                          🧬 {suggestion.substances_actives}
                                        </p>
                                      </div>
                                    )}
                                    
                                    {/* Infos AMM + Dose */}
                                    <div className="flex items-center justify-between mt-1.5">
                                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                                        <span className="text-green-600 font-medium">✓ AMM: {suggestion.numero_amm}</span>
                                        {suggestion.fonction && (
                                          <Badge variant="secondary" className="text-[9px] py-0 px-1">
                                            {suggestion.fonction}
                                          </Badge>
                                        )}
                                      </div>
                                      {suggestion.dose_max && (
                                        <Badge className="text-[10px] py-0 bg-primary/20 text-primary">
                                          Max {suggestion.dose_max} {suggestion.unite_dose || 'L/ha'}
                                        </Badge>
                                      )}
                                    </div>
                                    
                                    {/* Liens fiches + Firme */}
                                    <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-dashed">
                                      <span className="text-[10px] text-muted-foreground">{suggestion.firme}</span>
                                      <div className="flex items-center gap-2">
                                        {suggestion.url_fiche_ephy && (
                                          <a 
                                            href={suggestion.url_fiche_ephy}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            📋 Fiche technique
                                          </a>
                                        )}
                                        {suggestion.url_fds && (
                                          <a 
                                            href={suggestion.url_fds}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[10px] text-red-600 hover:underline flex items-center gap-0.5"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            🛡️ FDS
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Cultures autorisées */}
                                    {suggestion.cultures?.length > 0 && (
                                      <p className="text-[10px] text-muted-foreground mt-1">
                                        🌱 {suggestion.cultures.slice(0, 3).join(', ')}
                                        {suggestion.cultures.length > 3 && ` +${suggestion.cultures.length - 3}`}
                                      </p>
                                    )}
                                    
                                    {/* Statut */}
                                    {suggestion.statut === 'retire' && (
                                      <Badge variant="destructive" className="text-[9px] py-0 mt-1">
                                        ⚠️ Produit retiré
                                      </Badge>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Option de saisie manuelle si aucun résultat */}
                          {produit.showSuggestions && produit.nom?.length >= 2 && !produit.searching && (!Array.isArray(produit.suggestions) || produit.suggestions.length === 0) && (
                            <div className="absolute z-[300] w-full mt-1 bg-popover border border-amber-400 rounded-md shadow-lg">
                              <div className="p-3 text-center border-b bg-amber-50 dark:bg-amber-950/30">
                                <AlertCircle className="w-6 h-6 mx-auto mb-1 text-amber-500" />
                                <p className="text-xs font-medium text-amber-700 dark:text-amber-300">Aucun produit homologué trouvé</p>
                              </div>
                              <div
                                className="p-3 hover:bg-muted cursor-pointer transition-colors"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setFormData(prev => {
                                    const updated = [...prev.produits];
                                    updated[idx] = {
                                      ...updated[idx],
                                      showSuggestions: false,
                                      numero_amm: null,
                                      alerte: {
                                        type: 'danger',
                                        titre: '🔴 PRODUIT NON HOMOLOGUÉ',
                                        message: `"${updated[idx].nom}" n'est pas référencé dans la base EPHY officielle. Utilisation à vos risques - Non conforme pour le registre phytosanitaire.`
                                      }
                                    };
                                    return { ...prev, produits: updated };
                                  });
                                }}
                              >
                                <p className="text-xs font-medium">📝 Utiliser quand même "{produit.nom}"</p>
                                <p className="text-[10px] text-amber-600 mt-1">⚠️ Ce produit sera marqué comme non-homologué</p>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Statut du produit */}
                        {produit.numero_amm && (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-green-600 font-medium">✓ AMM: {produit.numero_amm}</span>
                            {produit.dose_recommandee && (
                              <span className="text-[10px] text-blue-600">📋 Dose max: {produit.dose_max} {produit.unite || 'L/ha'}</span>
                            )}
                          </div>
                        )}
                        {!produit.numero_amm && produit.nom && produit.nom.length > 3 && !produit.showSuggestions && (
                          <div className="mt-1 p-1.5 bg-amber-100 dark:bg-amber-900/40 rounded text-[10px] text-amber-700 dark:text-amber-300 font-medium">
                            ⚠️ PRODUIT NON RÉFÉRENCÉ EPHY - Vérifiez l'homologation
                          </div>
                        )}
                      </div>
                      
                      {/* Fournisseur */}
                      <div className="col-span-2 md:col-span-1">
                        <Label className="text-xs">Fournisseur</Label>
                        <Input
                          className="h-8 text-xs"
                          placeholder="Coop, Négoce..."
                          value={produit.fournisseur}
                          onChange={(e) => {
                            const updated = [...formData.produits];
                            updated[idx].fournisseur = e.target.value;
                            setFormData(prev => ({ ...prev, produits: updated }));
                          }}
                        />
                      </div>
                      
                      {/* Quantité + Unité avec vérification dose */}
                      <div className="flex gap-1">
                        <div className="flex-1">
                          <Label className="text-xs">Dose/ha</Label>
                          <Input
                            type="number"
                            step="0.1"
                            className={`h-8 text-xs ${produit.dose_depassee ? 'border-red-500 bg-red-50' : ''}`}
                            placeholder="Ex: 2.5"
                            value={produit.quantite}
                            onChange={(e) => {
                              const updated = [...formData.produits];
                              const dose = parseFloat(e.target.value) || 0;
                              updated[idx].quantite = e.target.value;
                              
                              // Vérification de la dose max
                              if (updated[idx].dose_max && dose > updated[idx].dose_max) {
                                updated[idx].dose_depassee = true;
                                updated[idx].alerte = {
                                  type: 'danger',
                                  titre: '🔴 DOSE MAXIMALE DÉPASSÉE',
                                  message: `La dose saisie (${dose} ${updated[idx].unite || 'L/ha'}) dépasse la dose homologuée (${updated[idx].dose_max} ${updated[idx].unite || 'L/ha'}). Non conforme au registre phyto.`
                                };
                              } else if (updated[idx].contient_cuivre && dose > 4) {
                                updated[idx].dose_depassee = true;
                                updated[idx].alerte = {
                                  type: 'warning',
                                  titre: '🟠 Limite cuivre annuelle',
                                  message: `Attention: ${dose} kg Cu/ha dépasse la limite UE de 4 kg Cu/ha/an. Vérifiez vos apports cumulés.`
                                };
                              } else {
                                updated[idx].dose_depassee = false;
                                // Conserver l'alerte info si cuivre
                                if (updated[idx].contient_cuivre) {
                                  updated[idx].alerte = {
                                    type: 'info',
                                    titre: '🟢 Produit à base de cuivre',
                                    message: `Dose OK. Limite UE: 4 kg Cu/ha/an.`
                                  };
                                } else if (updated[idx].numero_amm) {
                                  updated[idx].alerte = null;
                                }
                              }
                              
                              // Calcul auto du coût total
                              if (updated[idx].cout_unitaire) {
                                updated[idx].cout_total = (dose * parseFloat(updated[idx].cout_unitaire)).toFixed(2);
                              }
                              setFormData(prev => ({ ...prev, produits: updated }));
                            }}
                          />
                          {produit.dose_recommandee && !produit.dose_depassee && (
                            <p className="text-[10px] text-blue-600 mt-0.5">Recommandé: {produit.dose_recommandee}</p>
                          )}
                          {produit.dose_depassee && (
                            <p className="text-[10px] text-red-600 mt-0.5 font-semibold">⚠️ Max: {produit.dose_max}</p>
                          )}
                        </div>
                        <div className="w-20">
                          <Label className="text-xs">Unité</Label>
                          <Select
                            value={produit.unite}
                            onValueChange={(v) => {
                              const updated = [...formData.produits];
                              updated[idx].unite = v;
                              setFormData(prev => ({ ...prev, produits: updated }));
                            }}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="L/ha">L/ha</SelectItem>
                              <SelectItem value="kg/ha">kg/ha</SelectItem>
                              <SelectItem value="g/ha">g/ha</SelectItem>
                              <SelectItem value="mL/hL">mL/hL</SelectItem>
                              <SelectItem value="g/hL">g/hL</SelectItem>
                              <SelectItem value="kg">kg total</SelectItem>
                              <SelectItem value="L">L total</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Coût unitaire */}
                      <div>
                        <Label className="text-xs">€/unité</Label>
                        <Input
                          type="number"
                          step="0.01"
                          className="h-8 text-xs"
                          placeholder="5.50"
                          value={produit.cout_unitaire}
                          onChange={(e) => {
                            const updated = [...formData.produits];
                            updated[idx].cout_unitaire = e.target.value;
                            if (updated[idx].quantite) {
                              updated[idx].cout_total = (parseFloat(updated[idx].quantite) * parseFloat(e.target.value)).toFixed(2);
                            }
                            setFormData(prev => ({ ...prev, produits: updated }));
                          }}
                        />
                      </div>
                      
                      {/* Coût total + Calculette + Supprimer */}
                      <div className="flex items-end gap-1">
                        <div className="flex-1">
                          <Label className="text-xs">Total €</Label>
                          <Input
                            type="number"
                            step="0.01"
                            className="h-8 text-xs bg-muted"
                            value={produit.cout_total}
                            readOnly
                          />
                        </div>
                        {/* Bouton Calculette */}
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 bg-primary/10 hover:bg-primary/20 text-primary"
                          onClick={() => setShowCalculette({ produit, idx })}
                          title="Calculette pulvé"
                        >
                          <Calculator className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              produits: prev.produits.filter((_, i) => i !== idx)
                            }));
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
                
                {/* Résumé des coûts */}
                {(formData.produits || []).length > 0 && (
                  <div className="flex justify-end">
                    <div className="bg-primary/10 rounded-lg px-4 py-2">
                      <span className="text-sm text-muted-foreground mr-2">Coût total produits :</span>
                      <span className="font-bold text-lg">
                        {(formData.produits || []).reduce((sum, p) => sum + (parseFloat(p.cout_total) || 0), 0).toFixed(2)} €
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Calculette Produit - Modal */}
          {showCalculette && (
            <CalculetteProduit
              produit={showCalculette.produit}
              volumeBouillie={formData.volume_bouillie_ha}
              onClose={() => setShowCalculette(null)}
            />
          )}

          {/* Médias */}
          <div className="space-y-4">
            <Label>Médias (photos/vidéos)</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingMedia}
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Ajouter photo
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => videoInputRef.current?.click()}
                disabled={uploadingMedia}
              >
                <Video className="w-4 h-4 mr-2" />
                Ajouter vidéo
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    // Pour l'instant, on stocke juste le nom (l'upload se fera à la sauvegarde)
                    toast.info('La photo sera uploadée à l\'enregistrement');
                  }
                }}
              />
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    toast.info('La vidéo sera uploadée à l\'enregistrement');
                  }
                }}
              />
            </div>
            {/* Aperçu des images existantes */}
            {formData.images?.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {formData.images.map((img, idx) => (
                  <div key={idx} className="relative">
                    <img 
                      src={img.startsWith('/') ? `${API_URL}${img}` : img}
                      alt={`Photo ${idx + 1}`}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <button
                      type="button"
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        images: prev.images.filter((_, i) => i !== idx)
                      }))}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={saving} data-testid="btn-save-intervention">
              {saving ? 'Enregistrement...' : (intervention ? 'Modifier' : 'Créer')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function InterventionsPage() {
  const { token } = useAuth();
  const [interventions, setInterventions] = useState([]);
  const [parcelles, setParcelles] = useState([]);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [operations, setOperations] = useState({});
  const [materiels, setMateriels] = useState([]);
  const [intrants, setIntrants] = useState([]);
  const [commandes, setCommandes] = useState([]); // Commandes pour conditionnement
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIntervention, setEditingIntervention] = useState(null);
  const [stats, setStats] = useState(null);

  // Charger les données
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      setLoading(true);

      try {
        // Charger les opérations
        const opsRes = await fetch(`${API_URL}/api/team/operations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (opsRes.ok) {
          const data = await opsRes.json();
          setOperations(data.operations || {});
        }

        // Charger les interventions
        const intRes = await fetch(`${API_URL}/api/team/interventions/recentes?limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (intRes.ok) {
          const data = await intRes.json();
          setInterventions(data.interventions || []);
        }

        // Charger les parcelles
        const parcRes = await fetch(`${API_URL}/api/parcellaire/parcelles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (parcRes.ok) {
          const data = await parcRes.json();
          // L'API retourne {parcelles: [...], stats: {...}}
          setParcelles(data.parcelles || data || []);
        }

        // Charger les collaborateurs
        const collabRes = await fetch(`${API_URL}/api/team/collaborateurs`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (collabRes.ok) {
          const data = await collabRes.json();
          setCollaborateurs(data.collaborateurs || []);
        }

        // Charger les stats
        const statsRes = await fetch(`${API_URL}/api/team/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statsRes.ok) {
          const data = await statsRes.json();
          setStats(data);
        }

        // Charger matériels
        const matRes = await fetch(`${API_URL}/api/team/materiels`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (matRes.ok) {
          const data = await matRes.json();
          console.log('Matériels chargés:', data.materiels?.length || 0);
          setMateriels(data.materiels || []);
        } else {
          console.error('Erreur chargement matériels:', matRes.status);
        }

        // Charger intrants
        const intrantsRes = await fetch(`${API_URL}/api/team/intrants`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (intrantsRes.ok) {
          const data = await intrantsRes.json();
          setIntrants(data.intrants || []);
        }

        // Charger les commandes (pour les interventions de conditionnement)
        try {
          const commandesRes = await fetch(`${API_URL}/api/commandes`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (commandesRes.ok) {
            const data = await commandesRes.json();
            setCommandes(data.commandes || data || []);
          }
        } catch (e) {
          console.log('Commandes non disponibles');
        }
      } catch (error) {
        console.error('Erreur chargement:', error);
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  // Sauvegarder une intervention
  const handleSaveIntervention = async (formData, interventionId) => {
    try {
      const url = interventionId 
        ? `${API_URL}/api/team/interventions/${interventionId}`
        : `${API_URL}/api/team/interventions/rapide`;
      
      const method = interventionId ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const data = await res.json();
        
        if (interventionId) {
          setInterventions(prev => 
            prev.map(i => i.id === interventionId ? { ...i, ...formData } : i)
          );
          toast.success('Intervention modifiée');
        } else {
          // Recharger les interventions
          const intRes = await fetch(`${API_URL}/api/team/interventions/recentes?limit=100`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (intRes.ok) {
            const intData = await intRes.json();
            setInterventions(intData.interventions || []);
          }
          toast.success('Intervention créée');
        }
        
        setEditingIntervention(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  // Marquer comme réalisée
  const handleRealiser = async (interventionId) => {
    try {
      const res = await fetch(`${API_URL}/api/team/interventions/${interventionId}/realiser`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setInterventions(prev => 
          prev.map(i => i.id === interventionId 
            ? { ...i, statut_intervention: 'realisee', statut: 'terminee' } 
            : i
          )
        );
        toast.success('Intervention marquée comme réalisée');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Supprimer une intervention
  const handleDeleteIntervention = async (interventionId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer définitivement cette intervention ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/team/interventions/${interventionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setInterventions(prev => prev.filter(i => i.id !== interventionId));
        toast.success('Intervention supprimée');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Annuler (changer statut)
  const handleAnnuler = async (interventionId) => {
    if (!confirm('Voulez-vous vraiment annuler cette intervention ?')) return;

    try {
      const res = await fetch(`${API_URL}/api/team/interventions/${interventionId}/annuler`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setInterventions(prev => 
          prev.map(i => i.id === interventionId 
            ? { ...i, statut_intervention: 'annulee', statut: 'annulee' } 
            : i
          )
        );
        toast.success('Intervention annulée');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'annulation');
    }
  };

  // Supprimer définitivement (alias)
  const handleDelete = handleDeleteIntervention;

  // Valider
  const handleValider = async (interventionId) => {
    try {
      const res = await fetch(`${API_URL}/api/team/interventions/${interventionId}/valider`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setInterventions(prev => 
          prev.map(i => i.id === interventionId ? { ...i, validee: true } : i)
        );
        toast.success('Intervention validée');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la validation');
    }
  };

  // Filtrer les interventions
  const filteredInterventions = interventions.filter(i => {
    const matchSearch = 
      i.operation?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      i.nom_parcelle?.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchTab = true;
    if (activeTab === 'previsionnelle') matchTab = i.statut_intervention === 'previsionnelle';
    else if (activeTab === 'realisee') matchTab = i.statut_intervention === 'realisee';
    else if (activeTab === 'a_valider') matchTab = i.statut_intervention === 'realisee' && !i.validee;
    else if (activeTab !== 'all') matchTab = i.statut_intervention !== 'annulee';
    
    return matchSearch && matchTab;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="interventions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Interventions</h1>
          <p className="text-muted-foreground">
            Gérez vos interventions prévisionnelles et réalisées
          </p>
        </div>
        <Button 
          onClick={() => { setEditingIntervention(null); setDialogOpen(true); }}
          data-testid="btn-new-intervention"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle intervention
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="cursor-pointer hover:border-primary" onClick={() => setActiveTab('all')}>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.interventions?.cette_semaine || 0}</p>
                <p className="text-sm text-muted-foreground">Cette semaine</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-primary" onClick={() => setActiveTab('previsionnelle')}>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.interventions?.previsionnelles || 0}</p>
                <p className="text-sm text-muted-foreground">Prévisionnelles</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-primary" onClick={() => setActiveTab('realisee')}>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{stats.interventions?.realisees || 0}</p>
                <p className="text-sm text-muted-foreground">Réalisées</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-primary" onClick={() => setActiveTab('a_valider')}>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{stats.interventions?.a_valider || 0}</p>
                <p className="text-sm text-muted-foreground">À valider</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.interventions?.aujourdhui || 0}</p>
                <p className="text-sm text-muted-foreground">Aujourd'hui</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtres */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher une intervention..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-interventions"
          />
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full md:w-auto">
          <TabsList>
            <TabsTrigger value="all">Toutes</TabsTrigger>
            <TabsTrigger value="previsionnelle">Prévisionnelles</TabsTrigger>
            <TabsTrigger value="realisee">Réalisées</TabsTrigger>
            <TabsTrigger value="a_valider">À valider</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Liste des interventions */}
      {filteredInterventions.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {filteredInterventions.map((intervention) => (
            <InterventionCard
              key={intervention.id}
              intervention={intervention}
              operations={operations}
              onEdit={(i) => { setEditingIntervention(i); setDialogOpen(true); }}
              onRealiser={handleRealiser}
              onValider={handleValider}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucune intervention trouvée</p>
            <Button 
              variant="link" 
              onClick={() => { setEditingIntervention(null); setDialogOpen(true); }}
            >
              Créer la première intervention
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Dialog création/modification */}
      <InterventionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        intervention={editingIntervention}
        parcelles={parcelles}
        collaborateurs={collaborateurs}
        operations={operations}
        materiels={materiels}
        intrants={intrants}
        commandes={commandes}
        onSave={handleSaveIntervention}
      />
    </div>
  );
}
